import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DragStart, DragUpdate, DropResult } from 'react-beautiful-dnd';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ColumnDef, PaginationState, Row, SortingState } from '@tanstack/react-table';
import {
  Table, TableBody, TableCell, TableFooter, TableHeader, TableRow, SortIndicator,
} from '@/components/ui/Table';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import LoadingStatusBar from './LoadingStatusBar';
import PaginationControls from './PaginationControls';

const SORT_DIRECTION_MAP: Record<string, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
};

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface GlobalFilterConfig {
  placeholder?: string;
}

interface PaginationConfig {
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}

interface LoadingStatusConfig {
  isFullyLoaded: boolean;
  loadedCount: number;
  totalCount: number;
}

/** The row a drop moved: its `getRowId` (stringified) and its index before and after the drop. */
export interface RowMove {
  fromIndex: number;
  id: string;
  toIndex: number;
}

/**
 * A piece of reorder text: the drag handle's accessible name, or one step of a drag as it is
 * announced to a screen reader. `label` is the row's name from `getRowLabel`, empty when the
 * table doesn't supply one; `position` is 1-based.
 */
export interface ReorderText {
  kind: 'handleLabel' | 'lifted' | 'moved' | 'dropped' | 'cancelled' | 'failed';
  label: string;
  position: number;
  total: number;
}

// AIDEV-NOTE: rbd writes its own drag announcements, but appends the live region to document.body
// — outside any aria-modal dialog, where screen readers ignore it. A table that opens in a dialog
// (the slide builder's Group Slides popup) was silent for the whole drag, so the messages are
// rebuilt here and rendered inside the table's own frame.
const defaultReorderText = ({ kind, label, position, total }: ReorderText) => {
  const name = label || `row ${position}`;

  switch (kind) {
    case 'handleLabel':
      return label ? `Reorder ${label}` : 'Reorder row';
    case 'lifted':
      return `Picked up ${name}, position ${position} of ${total}. `
        + 'Use the arrow keys to move it, space to drop, escape to cancel.';
    case 'moved':
      return `Moved to position ${position} of ${total}.`;
    case 'dropped':
      return `Dropped ${name} at position ${position} of ${total}.`;
    case 'cancelled':
      return `Reorder cancelled. ${name} returned to position ${position} of ${total}.`;
    case 'failed':
      return `Could not save the new order. ${name} returned to position ${position} of ${total}.`;
    default:
      return '';
  }
};

interface ReorderConfig<T> {
  /** Stable id per row — the drag key. */
  getRowId: (row: T) => number | string;
  /**
   * The row's name, e.g. a title. Without it every handle is called "Reorder row" and a
   * screen-reader user can't tell which row they are lifting.
   */
  getRowLabel?: (row: T) => string;
  /**
   * Receives the table's data in its new order after a drop, plus which row moved. Return the
   * save's promise to get a brief accent flash on the moved row once it resolves.
   */
  onReorder: (rows: T[], move: RowMove) => void | Promise<unknown>;
  /**
   * Renders every reorder string. Defaults to English, which suits the sysadmin tables; a
   * translated surface passes its own so the handle name and the drag announcements follow the
   * viewer's locale.
   */
  text?: (text: ReorderText) => string;
}

interface SPADataTableProps<T> {
  actionSlot?: React.ReactNode;
  columns: ColumnDef<T, unknown>[];
  data: T[];
  defaultSort?: SortingState;
  emptyMessage?: string;
  /** Small note under the table, on the pagination row when there is one. */
  footnote?: React.ReactNode;
  globalFilter?: GlobalFilterConfig;
  isLoading?: boolean;
  /**
   * Skeleton *body* rows to show while loading — the header band is drawn regardless. Defaults to
   * 8, which suits a full page of results. Short tables should pass their real row count: inside an
   * auto-height dialog any difference is a visible resize, and a centred dialog moves vertically as
   * well as changing size.
   */
  loadingRows?: number;
  loadingStatus?: LoadingStatusConfig;
  meta?: object;
  pagination?: PaginationConfig;
  /**
   * Drag-to-reorder rows via a leading grip-handle column (only the handle drags, so rows
   * can't be moved by accident). Row order must be the data order: use with `defaultSort={[]}`,
   * no sortable columns, and no pagination.
   */
  reorder?: ReorderConfig<T>;
  rowClassName?: (row: T) => string;
  secondaryActionSlot?: React.ReactNode;
  /** Alternate row backgrounds. Off by default — dividers usually read better on their own. */
  striped?: boolean;
}

// AIDEV-NOTE: Mirrors the real table's frame and row heights — same bordered wrapper, a header
// band, then `rows` body bands — so the placeholder occupies the space the table will. Free-floating
// bars with their own padding overshot it, which inside an auto-height dialog is a visible resize.
//
// bg-spa-bg-skeleton, not bg-spa-bg-secondary: the latter is the token the dialog surface uses, so
// in a modal the bars pulsed invisibly against an identical colour.
// The frame, row, and cell classes are exported so list-shaped surfaces (SPARowList) match the
// table exactly instead of copying its strings.
export const dataFrameClass = cn(
  'tw-rounded-lg tw-overflow-x-auto tw-bg-spa-bg-secondary',
  'tw-border tw-border-solid tw-border-spa-border-secondary',
);

// The table's outer frame holds the toolbar strip, the scrolling table, and the footer strip, so
// the controls read as part of the table rather than floating on the ground around it. Overflow is
// hidden here only to clip the corners; the table scrolls inside its own region below.
const tableFrameClass = cn(
  'tw-rounded-lg tw-overflow-hidden tw-bg-spa-bg-secondary',
  'tw-border tw-border-solid tw-border-spa-border-secondary',
);

const stripClass = 'tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-4';
const stripRule = 'tw-border-0 tw-border-solid tw-border-spa-border-secondary';

export const dataRowClass = (index: number, striped: boolean) => cn(
  'tw-text-spa-primary tw-transition-colors hover:tw-bg-spa-bg-hover',
  // Transparent so the table takes whatever surface it sits on. Stripes have their own token, set
  // between the surface and bg-tertiary so they read quieter than the header band.
  striped && index % 2 === 1 ? 'tw-bg-spa-bg-stripe-even' : 'tw-bg-transparent',
);

export const dataCellClass = (isLastRow: boolean, align?: string) => cn(
  'tw-px-4 tw-py-3 tw-text-sm tw-align-middle',
  align === 'center' && 'tw-text-center',
  align === 'right' && 'tw-text-right tw-tabular-nums',
  // Last row leans on the wrapper border instead of drawing a double line.
  !isLastRow && 'tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-spa-border-secondary',
);

// Summary row under the body, drawn when any column defines a `footer`: a heavier rule above it
// mirrors the header's rule below.
export const dataFooterCellClass = (align?: string) => cn(
  'tw-px-4 tw-py-3 tw-text-sm tw-align-middle tw-font-semibold tw-text-spa-heading',
  align === 'center' && 'tw-text-center',
  align === 'right' && 'tw-text-right tw-tabular-nums',
  'tw-border-t-2 tw-border-solid tw-border-x-0 tw-border-b-0 tw-border-spa-border',
);

export const dataEmptyClass = 'tw-text-center tw-text-spa-tertiary tw-py-12 tw-border-0';

const bodyCellClass = (align: string | undefined, isLastRow: boolean) => dataCellClass(isLastRow, align);

const DataCells = <T extends object>({ isLastRow, row }: { isLastRow: boolean; row: Row<T> }) => (
  <>
    {row.getVisibleCells().map(cell => (
      <TableCell
        key={cell.id}
        className={bodyCellClass(cell.column.columnDef.meta?.align, isLastRow)}
        style={{ width: cell.column.columnDef.meta?.width ?? cell.column.getSize() }}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    ))}
  </>
);

const dragHandleClass = cn(
  'tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded',
  'tw-cursor-grab active:tw-cursor-grabbing tw-text-spa-tertiary tw-transition-colors',
  'hover:tw-bg-spa-bg-hover hover:tw-text-spa-primary',
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-spa-accent',
);

const LoadingSkeleton = ({ rows }: { rows: number }) => (
  <div>
    <div className="tw-h-10 tw-bg-spa-bg-skeleton tw-animate-pulse" />
    {Array.from({ length: rows }, (_, i) => (
      <div
        key={i}
        className={cn(
          'tw-h-14 tw-bg-spa-bg-skeleton-subtle tw-animate-pulse',
          'tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary',
        )}
      />
    ))}
  </div>
);

// AIDEV-NOTE: Reusable SPA table component wrapping @tanstack/react-table v8.
// Handles sorting, pagination, global search, loading states, and empty states.
// Consumers provide columns + data; toolbar/filters are composed by the parent.
const SPADataTable = <T extends object>({
  actionSlot,
  columns,
  data,
  defaultSort = [{ desc: false, id: 'title' }],
  emptyMessage = 'No results found.',
  footnote,
  globalFilter: globalFilterConfig,
  isLoading = false,
  loadingRows = 8,
  loadingStatus,
  meta,
  pagination: paginationConfig,
  reorder,
  rowClassName,
  secondaryActionSlot,
  striped = false,
}: SPADataTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>(defaultSort);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [savedRowId, setSavedRowId] = useState<string | null>(null);
  const savedFlashTimer = React.useRef<number | undefined>(undefined);
  const [dragMessage, setDragMessage] = useState('');
  const dragMessageTimer = React.useRef<number | undefined>(undefined);
  const dragging = React.useRef(false);

  // Drop commits ride the consumer's async data flow (query cache → props), which lands a paint
  // or two after rbd clears its drag transforms — a visible flash of the old order. This local
  // override is committed synchronously inside onDragEnd (same task as rbd's cleanup, so the
  // browser paints one state) and clears as soon as the data prop catches up.
  const [localOrder, setLocalOrder] = useState<T[] | null>(null);

  React.useEffect(() => {
    setLocalOrder(null);
  }, [data]);

  React.useEffect(() => () => window.clearTimeout(savedFlashTimer.current), []);
  React.useEffect(() => () => window.clearTimeout(dragMessageTimer.current), []);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: paginationConfig?.defaultPageSize ?? DEFAULT_PAGE_SIZE,
  });

  const rowModels = {
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(globalFilterConfig && { getFilteredRowModel: getFilteredRowModel() }),
    ...(paginationConfig && { getPaginationRowModel: getPaginationRowModel() }),
  };

  const state = {
    sorting,
    ...(globalFilterConfig && { globalFilter: globalFilterValue }),
    ...(paginationConfig && { pagination }),
  };

  const table = useReactTable({
    ...rowModels,
    autoResetPageIndex: true, // AIDEV-NOTE: Intentional — resets to page 1 on data/filter changes
    columns,
    data: (reorder && localOrder) || data,
    meta,
    onSortingChange: setSorting,
    state,
    ...(globalFilterConfig && {
      globalFilterFn: 'includesString' as const,
      onGlobalFilterChange: setGlobalFilterValue,
    }),
    ...(paginationConfig && { onPaginationChange: setPagination }),
  });

  const hasFooter = columns.some(column => column.footer !== undefined);

  const bodyRowClass = (row: Row<T>, index: number) => cn(
    dataRowClass(index, striped),
    rowClassName?.(row.original),
  );

  const showPagination = !isLoading && Boolean(paginationConfig) && table.getPageCount() > 1;

  const rowLabel = (draggableId: string) => {
    if (!reorder?.getRowLabel) return '';

    const row = table.getRowModel().rows
      .find(candidate => String(reorder.getRowId(candidate.original)) === draggableId);

    return row ? reorder.getRowLabel(row.original) : '';
  };

  const announceDrag = (kind: ReorderText['kind'], draggableId: string, position: number) => {
    if (!reorder) return;

    const text = reorder.text ?? defaultReorderText;
    const message = text({
      kind, label: rowLabel(draggableId), position, total: table.getRowModel().rows.length,
    });

    // A live region only speaks when its text changes, so an identical repeat — lifting the same
    // row twice after an escape — needs the region emptied before it is set again.
    window.clearTimeout(dragMessageTimer.current);
    setDragMessage('');
    dragMessageTimer.current = window.setTimeout(() => setDragMessage(message), 50);
  };

  // onBeforeDragStart, not onDragStart: rbd delivers onDragStart from a setTimeout, so the flag
  // would not be set yet if the user cancelled in the same task as the lift.
  const handleBeforeDragStart = () => {
    dragging.current = true;
  };

  const handleDragStart = (start: DragStart) => {
    announceDrag('lifted', start.draggableId, start.source.index + 1);
  };

  const handleDragUpdate = (update: DragUpdate) => {
    if (!update.destination) return;

    announceDrag('moved', update.draggableId, update.destination.index + 1);
  };

  const handleDragEnd = (result: DropResult) => {
    // AIDEV-NOTE: cleared on the next task, not now — the Escape that cancels a drag only reaches
    // our keydown handler after rbd's window-capture listener has already ended the drag here.
    window.setTimeout(() => { dragging.current = false; }, 0);

    if (!reorder) return;

    if (!result.destination || result.destination.index === result.source.index) {
      announceDrag('cancelled', result.draggableId, result.source.index + 1);

      return;
    }

    announceDrag('dropped', result.draggableId, result.destination.index + 1);

    const next = table.getRowModel().rows.map(row => row.original);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setLocalOrder(next);

    const move: RowMove = {
      fromIndex: result.source.index, id: result.draggableId, toIndex: result.destination.index,
    };

    Promise.resolve(reorder.onReorder(next, move))
      .then(() => {
        setSavedRowId(result.draggableId);
        window.clearTimeout(savedFlashTimer.current);
        // Outlives the 2.2s animation so removing the class never clips the fade.
        savedFlashTimer.current = window.setTimeout(() => setSavedRowId(null), 2400);
      })
      // A failed save reverts via the consumer; no flash. The rollback is silent on screen
      // otherwise — the error toast announces from outside the dialog, where this table may sit.
      .catch(() => announceDrag('failed', result.draggableId, result.source.index + 1));
  };

  const globalSearch = globalFilterConfig && (
    <input
      aria-label={globalFilterConfig.placeholder ?? 'Search'}
      className={cn(
        'tw-bg-spa-bg-secondary tw-border tw-border-solid tw-border-spa-border tw-rounded tw-px-3 tw-py-1.5',
        'tw-text-spa-on-bg tw-text-sm tw-w-64 tw-box-border',
        'placeholder:tw-text-spa-tertiary focus:tw-outline-none focus:tw-border-spa-accent',
      )}
      placeholder={globalFilterConfig.placeholder ?? 'Search...'}
      type="text"
      value={globalFilterValue}
      onChange={e => setGlobalFilterValue(e.target.value)}
    />
  );

  // rbd cancels the drag itself from a window-capture listener; react-modal would otherwise see
  // the same Escape bubble up and close the whole dialog out from under the drag.
  const swallowEscapeWhileDragging = (e: React.KeyboardEvent) => {
    if (dragging.current && e.key === 'Escape') e.stopPropagation();
  };

  return (
    // The frame isn't a widget — the handler only keeps a key press from escaping the table, so the
    // rule's role/keyboard-handler pairing doesn't apply.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={tableFrameClass} onKeyDown={swallowEscapeWhileDragging}>
      {/* Inside the frame on purpose: a table can open in an aria-modal dialog, and a live region
          outside that dialog is never read. */}
      {reorder && <div aria-live="polite" className="tw-sr-only" role="status">{dragMessage}</div>}

      {/* One strip: search and the primary action on the left, secondary controls and the
          results/per-page status on the right. */}
      {(globalSearch || actionSlot || secondaryActionSlot || (loadingStatus && paginationConfig)) && (
        <div className={cn(stripClass, stripRule, 'tw-border-b tw-py-3')}>
          <div className="tw-flex tw-items-center tw-gap-3">
            {globalSearch}
            {actionSlot}
          </div>
          <div className="tw-flex tw-items-center tw-gap-4">
            {secondaryActionSlot}
            {loadingStatus && paginationConfig && (
              <LoadingStatusBar
                isFullyLoaded={loadingStatus.isFullyLoaded}
                loadedCount={loadingStatus.loadedCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={paginationConfig.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS}
                totalCount={loadingStatus.totalCount}
                onPageSizeChange={size => setPagination({ pageIndex: 0, pageSize: size })}
              />
            )}
          </div>
        </div>
      )}

      {/* AIDEV-NOTE: Only the table swaps for the skeleton — the toolbar strip stays mounted while
          loading, so it does not pop in with the rows. The skeleton draws the same bands inside the
          same frame, so nothing shifts when the real table replaces it. */}
      {isLoading ? <LoadingSkeleton rows={loadingRows} /> : (
      /* AIDEV-NOTE: This region must be overflow-x-auto: below the table's min-content width the
         columns stop compressing and the table overflows, and `hidden` would clip that off with no
         way to reach it. Scrolling the table inside its own region also keeps the page itself free
         of a horizontal scrollbar, which body.sysadmin-spa suppresses anyway. */
        <div className="tw-overflow-x-auto">
          <Table className="tw-w-full tw-border-collapse">
            <TableHeader
              className={cn(
                'tw-bg-spa-bg-tertiary',
                'tw-border-b-2 tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-spa-border',
              )}
            >
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {reorder && (
                    // tw-relative contains the absolute sr-only span (see NumberCell).
                    <TableCell header className="tw-relative tw-w-10 tw-px-2 tw-py-3">
                      <span className="tw-sr-only">Reorder</span>
                    </TableCell>
                  )}
                  {headerGroup.headers.map(header => (
                    <TableCell
                      key={header.id}
                      header
                      aria-sort={
                      SORT_DIRECTION_MAP[header.column.getIsSorted() as string]
                      ?? (header.column.getCanSort() ? 'none' : undefined)
                    }
                      className={cn(
                        'tw-text-spa-heading tw-text-xs tw-uppercase tw-tracking-wider tw-font-semibold',
                        'tw-px-4 tw-py-3',
                        header.column.getCanSort() && 'tw-cursor-pointer tw-select-none',
                      )}
                      style={{ width: header.column.columnDef.meta?.width ?? header.getSize() }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span
                        className={cn(
                          'tw-flex tw-items-center',
                          header.column.columnDef.meta?.align === 'center' && 'tw-justify-center',
                          header.column.columnDef.meta?.align === 'right' && 'tw-justify-end',
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                        <SortIndicator direction={header.column.getIsSorted()} />
                        )}
                      </span>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            {reorder && table.getRowModel().rows.length > 0 ? (
              // Raw tbody/tr here: react-beautiful-dnd needs the real DOM nodes via innerRef,
              // and the ui/Table wrappers don't forward refs (they add no styling of their own).
              <DragDropContext
                onBeforeDragStart={handleBeforeDragStart}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                onDragUpdate={handleDragUpdate}
              >
                <Droppable droppableId="spa-data-table">
                  {droppableProvided => (
                    <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                      {table.getRowModel().rows.map((row, index) => (
                        <Draggable
                          key={String(reorder.getRowId(row.original))}
                          draggableId={String(reorder.getRowId(row.original))}
                          index={index}
                        >
                          {(dragProvided, snapshot) => (
                            <tr
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={cn(
                                bodyRowClass(row, index),
                                // The dragged row leaves the table flow — give it a solid surface.
                                snapshot.isDragging && 'tw-bg-spa-bg-secondary tw-shadow-lg',
                                // Success pulse once the reorder save resolves.
                                String(reorder.getRowId(row.original)) === savedRowId
                                && 'spa-saved-flash',
                              )}
                            >
                              <td
                                className={bodyCellClass(
                                  undefined,
                                  index === table.getRowModel().rows.length - 1,
                                )}
                              >
                                <div
                                  {...dragProvided.dragHandleProps}
                                  aria-label={(reorder.text ?? defaultReorderText)({
                                    kind: 'handleLabel',
                                    label: reorder.getRowLabel?.(row.original) ?? '',
                                    position: index + 1,
                                    total: table.getRowModel().rows.length,
                                  })}
                                  className={dragHandleClass}
                                >
                                  <Icon icon="Drag" size={16} />
                                </div>
                              </td>
                              <DataCells
                                isLastRow={index === table.getRowModel().rows.length - 1}
                                row={row}
                              />
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      className={dataEmptyClass}
                      colSpan={columns.length + (reorder ? 1 : 0)}
                      role="status"
                    >
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row, index) => (
                    <TableRow key={row.id} className={bodyRowClass(row, index)}>
                      <DataCells
                        isLastRow={index === table.getRowModel().rows.length - 1}
                        row={row}
                      />
                    </TableRow>
                  ))
                )}
              </TableBody>
            )}

            {hasFooter && table.getRowModel().rows.length > 0 && (
              <TableFooter>
                {table.getFooterGroups().map(footerGroup => (
                  <TableRow key={footerGroup.id}>
                    {reorder && <TableCell className={dataFooterCellClass()} />}
                    {footerGroup.headers.map(footer => (
                      <TableCell
                        key={footer.id}
                        className={dataFooterCellClass(footer.column.columnDef.meta?.align)}
                      >
                        {flexRender(footer.column.columnDef.footer, footer.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableFooter>
            )}
          </Table>
        </div>
      )}

      {/* Footnote and pagination share one strip so a note doesn't add its own band under the table.
          The controls render nothing on a single page, so the strip only appears when it has content. */}
      {(footnote || showPagination) && (
        <div className={cn(stripClass, stripRule, 'tw-border-t tw-py-2.5')}>
          <span className="tw-text-xs tw-text-spa-tertiary">{footnote}</span>
          {showPagination && (
            <PaginationControls
              canNextPage={table.getCanNextPage()}
              canPreviousPage={table.getCanPreviousPage()}
              pageCount={table.getPageCount()}
              pageIndex={pagination.pageIndex}
              onNextPage={() => table.nextPage()}
              onPageChange={page => table.setPageIndex(page)}
              onPreviousPage={() => table.previousPage()}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SPADataTable;
